import { z } from 'zod';
import { PRODUCT_TYPE_CODES } from './product-types.js';
import {
  CONSENT_VALUES,
  MAX_ADMIN_NOTES_CHARS,
  MAX_COMMENT_CHARS,
  MAX_DISPLAY_NAME_CHARS,
  MAX_PHOTO_BYTES,
  PHOTO_CONTENT_TYPES,
  REQUEST_STATUSES,
  REVIEW_STATUSES,
  TERNARY_ANSWERS,
} from './enums.js';

const ratingSchema = z.number().int().min(1).max(5);
const optionalRatingSchema = z.union([ratingSchema, z.null()]).optional();

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value ? value : undefined));

const optionalEmail = z
  .union([z.literal(''), z.string().trim().email().max(254)])
  .optional()
  .transform((value) => (value ? value : undefined));

export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.literal('fois-utsavvoice'),
  environment: z.string().min(1),
  timestamp: z.string().min(1),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const adminSessionResponseSchema = z.object({
  authenticated: z.literal(true),
  subject: z.string().min(1),
  username: z.string().optional(),
  email: z.string().optional(),
  groups: z.array(z.string()),
});

export type AdminSessionResponse = z.infer<typeof adminSessionResponseSchema>;

export const createFeedbackRequestBodySchema = z
  .object({
    orderReference: z.string().trim().min(1).max(120),
    productName: z.string().trim().min(1).max(200),
    productType: z.enum(PRODUCT_TYPE_CODES),
    customerName: optionalText(120),
    customerEmail: optionalEmail,
    expirationDays: z.union([z.literal(30), z.literal(60), z.literal(90)]).optional(),
  })
  .strict();

export const publicFeedbackRequestSchema = z.object({
  requestId: z.string().uuid(),
  productName: z.string(),
  status: z.enum(REQUEST_STATUSES),
  photoUploadEnabled: z.boolean(),
});

export const photoUploadUrlRequestSchema = z
  .object({
    contentType: z.enum(PHOTO_CONTENT_TYPES),
    fileSize: z.number().int().positive().max(MAX_PHOTO_BYTES),
    fileExtension: z.enum(['jpg', 'jpeg', 'png', 'webp']),
  })
  .strict();

export const submitFeedbackBodySchema = z
  .object({
    overallRating: ratingSchema,
    productRating: ratingSchema,
    packagingRating: ratingSchema,
    valueRating: ratingSchema,
    deliveryRating: optionalRatingSchema,
    likedMost: optionalText(MAX_COMMENT_CHARS),
    improvement: optionalText(MAX_COMMENT_CHARS),
    recommendation: z.enum(TERNARY_ANSWERS),
    purchaseAgain: z.enum(TERNARY_ANSWERS),
    displayName: optionalText(MAX_DISPLAY_NAME_CHARS),
    consent: z.enum(CONSENT_VALUES),
    photoObjectId: z.string().min(1).nullable().optional(),
  })
  .strict();

export const updateResponseBodySchema = z
  .object({
    reviewStatus: z.enum(REVIEW_STATUSES).optional(),
    testimonialApproved: z.boolean().optional(),
    adminNotes: z.string().trim().max(MAX_ADMIN_NOTES_CHARS).optional(),
  })
  .strict();

export const adminFeedbackRequestSchema = z.object({
  requestId: z.string().uuid(),
  orderReference: z.string(),
  productName: z.string(),
  productType: z.enum(PRODUCT_TYPE_CODES),
  customerName: z.string().optional(),
  customerEmail: z.string().optional(),
  status: z.enum(REQUEST_STATUSES),
  expiresAt: z.number().int(),
  createdAt: z.string(),
  createdBy: z.string(),
  submittedAt: z.string().optional(),
});

export const createdFeedbackRequestSchema = adminFeedbackRequestSchema.extend({
  token: z.string().min(1),
  feedbackPath: z.string().min(1),
});

export const listFeedbackRequestsResponseSchema = z.object({
  items: z.array(adminFeedbackRequestSchema),
  nextCursor: z.string().nullable(),
});

export const submitFeedbackResponseSchema = z.object({
  responseId: z.string().uuid(),
  requestId: z.string().uuid(),
  submittedAt: z.string(),
  status: z.literal('SUBMITTED'),
});

export const photoUploadUrlResponseSchema = z.object({
  photoObjectId: z.string().min(1),
  uploadUrl: z.string().min(1),
  headers: z.record(z.string()),
  expiresInSeconds: z.number().int().positive(),
});

export const adminFeedbackResponseSchema = z.object({
  responseId: z.string().uuid(),
  requestId: z.string().uuid(),
  orderReference: z.string(),
  productName: z.string(),
  productType: z.enum(PRODUCT_TYPE_CODES),
  overallRating: z.number().int(),
  productRating: z.number().int(),
  packagingRating: z.number().int(),
  valueRating: z.number().int(),
  deliveryRating: z.number().int().optional(),
  likedMost: z.string().optional(),
  improvement: z.string().optional(),
  recommendation: z.enum(TERNARY_ANSWERS),
  purchaseAgain: z.enum(TERNARY_ANSWERS),
  displayName: z.string().optional(),
  consent: z.enum(CONSENT_VALUES),
  hasPhoto: z.boolean(),
  reviewStatus: z.enum(REVIEW_STATUSES),
  testimonialApproved: z.boolean(),
  adminNotes: z.string().optional(),
  submittedAt: z.string(),
  updatedAt: z.string(),
});

export const listFeedbackResponsesResponseSchema = z.object({
  items: z.array(adminFeedbackResponseSchema),
  nextCursor: z.string().nullable(),
});

export const dashboardSummarySchema = z.object({
  totalRequests: z.number().int().nonnegative(),
  totalResponses: z.number().int().nonnegative(),
  completionRate: z.number().nonnegative(),
  averageOverallRating: z.number().nullable(),
  averagePackagingRating: z.number().nullable(),
  averageProductRating: z.number().nullable(),
  averageValueRating: z.number().nullable(),
  recommendYesPercent: z.number().nullable(),
  purchaseAgainYesPercent: z.number().nullable(),
});

export const photoReadUrlResponseSchema = z.object({
  url: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
});

export type CreateFeedbackRequestBody = z.infer<typeof createFeedbackRequestBodySchema>;
export type PublicFeedbackRequest = z.infer<typeof publicFeedbackRequestSchema>;
export type PhotoUploadUrlRequest = z.infer<typeof photoUploadUrlRequestSchema>;
export type SubmitFeedbackBody = z.infer<typeof submitFeedbackBodySchema>;
export type UpdateResponseBody = z.infer<typeof updateResponseBodySchema>;
export type AdminFeedbackRequest = z.infer<typeof adminFeedbackRequestSchema>;
export type CreatedFeedbackRequest = z.infer<typeof createdFeedbackRequestSchema>;
export type ListFeedbackRequestsResponse = z.infer<typeof listFeedbackRequestsResponseSchema>;
export type SubmitFeedbackResponse = z.infer<typeof submitFeedbackResponseSchema>;
export type PhotoUploadUrlResponse = z.infer<typeof photoUploadUrlResponseSchema>;
export type AdminFeedbackResponse = z.infer<typeof adminFeedbackResponseSchema>;
export type ListFeedbackResponsesResponse = z.infer<typeof listFeedbackResponsesResponseSchema>;
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
export type PhotoReadUrlResponse = z.infer<typeof photoReadUrlResponseSchema>;
